// VersionLabel — the always-visible version at the foot of the sidebar, pure.
//
// Shows the number only ("Version 0.5.0", or just major.minor "0.5" when
// `short`, for the folded menu, pen I-12); the full number and the build
// appear when copied and in About. A click
// reports UP (`onCopy`); the shell writes the clipboard and confirms with a
// toast. Without `onCopy` it is plain text.

import { Trans, useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import "./VersionLabel.css";

export type VersionLabelProps = {
    readonly version: string;
    readonly short?: boolean;
    readonly onCopy?: () => void;
};

// "0.5.0" → "0.5"; anything without two dots stays as it is.
const majorMinor = (version: string): string =>
    version.split(".").slice(0, 2).join(".");

export const VersionLabel = ({
    version,
    short = false,
    onCopy,
}: VersionLabelProps): ReactNode => {
    const { t } = useLingui();
    // The number is mono (it reads as an identifier); the word is not. The
    // short form (the rail) stands alone, in the sans (pen "Shell · Rail").
    const number = (
        <span
            className="bilda-version-label__number"
            data-short={short ? "true" : undefined}
        >
            {short ? majorMinor(version) : version}
        </span>
    );
    const label = short ? number : <Trans>Version {number}</Trans>;
    return onCopy === undefined ? (
        <span className="bilda-version-label">{label}</span>
    ) : (
        <button
            type="button"
            className="bilda-version-label"
            title={t`Copy version`}
            aria-label={t`Version ${version}, copy`}
            onClick={onCopy}
        >
            {label}
        </button>
    );
};
